import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import type { ZuubPatientDetailDataV4 } from '../../@types/zuub/contracts';
import { getReactQueryAxiosInstance } from '../api';
import { endpoints } from '../endpoints';

export interface PushBenefitsToPMSRequest {
  /** Provide blob from DOM capture (e.g. pdfContext.generatePdfFromDom). Required at runtime. */
  getBlob?: () => Promise<Blob>;
  /** Optional plan (V4) for metadata. */
  plan?: ZuubPatientDetailDataV4;
  unifiedAppointmentId: string;
  pmsPatientId?: string;
}

export interface PushBenefitsToPMSResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * Generates the Benefits PDF on the client, uploads it to S3 (upload-print-view),
 * then calls writeback with the returned practiceDocumentId to push to PMS.
 */
export const usePushBenefitsToPMS = (
  options?: Parameters<
    typeof useMutation<
      PushBenefitsToPMSResponse,
      AxiosError,
      PushBenefitsToPMSRequest
    >
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: PushBenefitsToPMSRequest,
    ): Promise<PushBenefitsToPMSResponse> => {
      if (!currentOrganizationId || !currentPracticeId) {
        throw new Error('Missing organization or practice information');
      }

      const instance = getReactQueryAxiosInstance();

      if (!request.getBlob) {
        throw new Error('getBlob is required to push benefits to PMS');
      }
      // 1. Get PDF blob from DOM capture
      const pdfBlob = await request.getBlob();

      // 2. Upload PDF to get practiceDocumentId
      const formData = new FormData();
      formData.append('file', pdfBlob, 'benefits-summary.pdf');
      formData.append('unifiedAppointmentId', request.unifiedAppointmentId);
      if (request.pmsPatientId) {
        formData.append('pmsPatientId', request.pmsPatientId);
      }

      const uploadResponse = await instance.request<{
        practiceDocumentId: string;
      }>({
        method: 'POST',
        url: `/${endpoints.benefitsPdfUpload}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
          // Let browser set multipart/form-data with boundary (instance default is application/json and breaks FormData)
          'Content-Type': false as unknown as string,
        } as Record<string, string>,
        data: formData,
      });

      const practiceDocumentId = uploadResponse.data?.practiceDocumentId;
      if (!practiceDocumentId) {
        throw new Error('No practiceDocumentId in upload response');
      }

      // 3. Push to PMS via writeback
      await instance.request({
        method: 'POST',
        url: `/${endpoints.benefitsWriteback}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: {
          practiceDocumentId,
        },
      });

      return {
        success: true,
        message: t`Successfully pushed to PMS`,
      };
    },
    ...options,
  });
};
